import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { EvacZone, EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import { TFunction } from 'i18next';
import { TypographyWithTooltip } from 'components/TypographyWithTooltip';
import { ManagingGeoEventsByZoneUid } from 'features/evacZones/types';
import { getViewGeoEventLink } from 'features/geoEvents/geoEvent.utils';
import { statusSortingComparator } from 'features/evacZones/utils/statusSortingComparator';
import { StatusChip } from './StatusChip';
import { EvacZoneLevelSelect } from './EvacZoneLevelSelect';
import { formatTimeAgo, compareZonesByUpdatesAndStatus } from './utils';

export type EvacuationZonesTableRowData = {
  id: string;
  zone: string;
  updates: EvacZone;
  externalStatus?: EvacZoneStatus;
  internalStatus?: EvacZoneStatus;
  status?: EvacZoneStatus;
  evacZoneStyle: RegionEvacZoneStyle;
  hasUpdates: boolean;
  requiresApproval: boolean;
};

type EvacZoneTableCellParams<V> = GridRenderCellParams<
  EvacuationZonesTableRowData,
  V
>;

export const getGridColumnConfiguration = (
  t: TFunction,
  handleStatusChange: (id: string, newStatus: EvacZoneStatus) => number,
  disabled: boolean,
  managingGeoEvents: ManagingGeoEventsByZoneUid,
): GridColDef<EvacuationZonesTableRowData>[] => {
  return [
    {
      field: 'zone',
      headerName: t('geoEventEvacuations.evacZones.table.headers.zone'),
      flex: 10,
      sortable: true,
      disableColumnMenu: true,
      renderCell: ({ value: displayName }: EvacZoneTableCellParams<string>) => (
        <Typography fontWeight={500} noWrap={false}>
          {displayName}
        </Typography>
      ),
      getApplyQuickFilterFn: (filterValue: string) => {
        if (!filterValue) {
          return null;
        }
        return (value) => {
          return value.toLowerCase().includes(filterValue.toLowerCase());
        };
      },
    },
    {
      field: 'updates',
      headerName: t('geoEventEvacuations.evacZones.table.headers.updated'),
      flex: 8,
      renderCell: ({ row }: EvacZoneTableCellParams<EvacZone>) => {
        const zone = row.updates;
        if (!zone.dateModified) {
          return null;
        }
        const timeAgo = formatTimeAgo(zone.dateModified);
        return <Typography>{timeAgo}</Typography>;
      },
      sortComparator: compareZonesByUpdatesAndStatus,
      sortable: true,
      disableColumnMenu: true,
      getApplyQuickFilterFn: () => null,
    },
    {
      // This column is used for filtering only, not displayed
      field: 'hasUpdates',
      headerName: t('geoEventEvacuations.evacZones.table.headers.updated'),
      type: 'boolean',
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'externalStatus',
      headerName: t(
        'geoEventEvacuations.evacZones.table.headers.externalStatus',
      ),
      renderCell: ({
        value: externalStatus,
        row,
      }: EvacZoneTableCellParams<EvacZoneStatus | undefined>) => (
        <StatusChip
          evacZoneStatus={externalStatus}
          evacZoneStyle={row.evacZoneStyle}
        />
      ),
      sortable: true,
      sortComparator: statusSortingComparator,
      disableColumnMenu: true,
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'internalStatus',
      headerName: t(
        'geoEventEvacuations.evacZones.table.headers.internalStatus',
      ),
      renderCell: ({
        value: internalStatus,
        row,
      }: EvacZoneTableCellParams<EvacZoneStatus | undefined>) => (
        <StatusChip
          evacZoneStatus={internalStatus}
          evacZoneStyle={row.evacZoneStyle}
        />
      ),
      sortable: true,
      sortComparator: statusSortingComparator,
      disableColumnMenu: true,
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'action',
      width: 140,
      headerName: t('geoEventEvacuations.evacZones.table.headers.action'),
      renderCell: ({ row }: EvacZoneTableCellParams<undefined>) => {
        if (row.updates.isLocked) {
          const managingGeoEvent = managingGeoEvents.get(row.updates.uidV2);
          if (!managingGeoEvent) {
            return (
              <Typography variant="body2" color="textSecondary">
                {t('geoEventEvacuations.evacZones.table.locked')}
              </Typography>
            );
          }
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="textSecondary">
                {t('geoEventEvacuations.evacZones.table.managedBy')}
              </Typography>
              <TypographyWithTooltip
                variant="body2"
                noWrap
                title={managingGeoEvent.name}
              >
                <MuiLink
                  component={Link}
                  to={getViewGeoEventLink(
                    managingGeoEvent.geoEventType,
                    managingGeoEvent.id,
                  )}
                  target="_blank"
                  rel="noopener"
                >
                  {managingGeoEvent.name}
                </MuiLink>
              </TypographyWithTooltip>
            </Box>
          );
        }
        if (row.requiresApproval) {
          return (
            <Typography variant="body2" color="textSecondary">
              {t('geoEventEvacuations.evacZones.table.pending')}
            </Typography>
          );
        }
        return (
          <EvacZoneLevelSelect
            value={row.status || ''}
            internalStatus={row.internalStatus || ''}
            evacZoneStyle={row.evacZoneStyle}
            disabled={disabled}
            onChange={(newStatus) =>
              handleStatusChange(row.id as string, newStatus)
            }
          />
        );
      },
      sortable: false,
      disableColumnMenu: true,
      getApplyQuickFilterFn: () => null,
    },
  ];
};
